import { LedgerEntry_UomSnapshot } from "gen/ledger/models_pb"
import { Product } from "gen/product/models_pb"
import { UUID as PBUUID } from "gen/proto/uuid/models_pb"

import {
  ProductUnitOfMeasureConversion,
  UnitOfMeasure,
} from "./unitsOfMeasure.types"
import { Decimal } from "../../classes/decimal"
import { FractionalMoney, Money } from "../../classes/money"
import { UUID } from "../../classes/uuid"
import { MAX_DECIMAL_QUANTITY_PLACES } from "../../utils/constants"

export const defaultUnitOfMeasureLabel = "Unit (U)"
export const defaultUnitOfMeasureName = "Unit"
export const defaultUnitOfMeasureCode = "U"

export function getUnitOfMeasureLabel(unitOfMeasure?: UnitOfMeasure) {
  return unitOfMeasure
    ? `${unitOfMeasure.name} (${unitOfMeasure.code})`
    : defaultUnitOfMeasureLabel
}

export function getUnitOfMeasureName(unitOfMeasure?: UnitOfMeasure) {
  return unitOfMeasure ? unitOfMeasure.name : defaultUnitOfMeasureName
}

export function getUnitOfMeasureCode(unitOfMeasure?: UnitOfMeasure) {
  return unitOfMeasure ? unitOfMeasure.code : defaultUnitOfMeasureCode
}

export function getConversionFactorForUom(
  product?: Pick<Product, "unitConversions">,
  uomId?: PBUUID
): Decimal | undefined {
  if (!product || !uomId) return undefined

  const conversionFactor = product.unitConversions?.find((uc) =>
    UUID.eqFromPB(uc.uomId, uomId)
  )?.conversionFactor

  return conversionFactor ? Decimal.fromPB(conversionFactor) : undefined
}

export function findConversionForUom(
  conversions: ProductUnitOfMeasureConversion[],
  uomId: UUID
): ProductUnitOfMeasureConversion | undefined {
  return conversions.find((c) => c.unitOfMeasure?.id.eq(uomId))
}

export function safeConvertQuantityToUoMQuantity(
  quantity: Decimal,
  conversionFactor?: Decimal
): Decimal {
  const factor =
    conversionFactor && conversionFactor.gt(Decimal.zero())
      ? conversionFactor
      : Decimal.fromBigSource(1)
  return quantity.div(factor).round(MAX_DECIMAL_QUANTITY_PLACES)
}

export function convertUoMQuantityToQuantity(
  uomQuantity: Decimal,
  conversionFactor?: Decimal
): Decimal {
  const factor = conversionFactor ?? Decimal.fromBigSource(1)
  return uomQuantity.mult(factor)
}

export function convertBaseCostToUoMCost(
  baseCost: FractionalMoney,
  conversionFactor?: Decimal
): FractionalMoney {
  const factor = conversionFactor ?? Decimal.fromBigSource(1)
  return baseCost.mult(factor)
}

export function convertUoMCostToBaseCost(
  uomCost: FractionalMoney,
  conversionFactor?: Decimal
): FractionalMoney {
  const factor =
    conversionFactor && conversionFactor.gt(Decimal.zero())
      ? conversionFactor
      : Decimal.fromBigSource(1)
  return uomCost.div(factor)
}

export function convertBasePriceToUoMPrice(
  basePrice: Money,
  conversionFactor?: Decimal
): Money
export function convertBasePriceToUoMPrice(
  basePrice: FractionalMoney,
  conversionFactor?: Decimal
): Money
export function convertBasePriceToUoMPrice(
  basePrice: Money | FractionalMoney,
  conversionFactor?: Decimal
): Money {
  const factor = conversionFactor ?? Decimal.fromBigSource(1)
  const scaled = basePrice.mult(factor)
  return scaled instanceof FractionalMoney ? scaled.toMoney() : scaled
}

export function isValidUomSnapshot(
  snapshot?: LedgerEntry_UomSnapshot
): snapshot is LedgerEntry_UomSnapshot {
  if (!snapshot) return false
  if (!snapshot.uomCode || !snapshot.uomName) return false
  if (!snapshot.conversionFactorToBase) return false
  return Decimal.fromPB(snapshot.conversionFactorToBase).gt(Decimal.zero())
}

export type UomSelectionValidationResult =
  | {
      ok: true
      uomId: PBUUID
      uomSnapshot: LedgerEntry_UomSnapshot
    }
  | {
      ok: false
      reason:
        | "missing_uom_id"
        | "missing_product"
        | "missing_conversion_factor"
        | "invalid_conversion_factor"
        | "missing_unit"
    }

export function validateUomSelection(args: {
  product?: Pick<Product, "unitConversions">
  uomId?: PBUUID
  companyUnitsOfMeasureById: Map<string, UnitOfMeasure>
}): UomSelectionValidationResult {
  const { product, uomId, companyUnitsOfMeasureById } = args

  if (!uomId) return { ok: false, reason: "missing_uom_id" }
  if (!product) return { ok: false, reason: "missing_product" }

  const conversionFactorToBasePB = product.unitConversions?.find((uc) =>
    UUID.eqFromPB(uc.uomId, uomId)
  )?.conversionFactor

  if (!conversionFactorToBasePB) {
    return { ok: false, reason: "missing_conversion_factor" }
  }
  if (!Decimal.fromPB(conversionFactorToBasePB).gt(Decimal.zero())) {
    return { ok: false, reason: "invalid_conversion_factor" }
  }

  const unit = companyUnitsOfMeasureById.get(UUID.idString(uomId))
  if (!unit?.code || !unit?.name) {
    return { ok: false, reason: "missing_unit" }
  }

  return {
    ok: true,
    uomId,
    uomSnapshot: {
      uomCode: unit.code,
      uomName: unit.name,
      conversionFactorToBase: conversionFactorToBasePB,
    },
  }
}

export function isQuantityDivisibleByFactor(
  quantity: Decimal,
  factor: Decimal
): boolean {
  if (factor.isZero()) {
    return false
  }

  const convertedQuantity = quantity.div(factor)
  return convertedQuantity.eq(convertedQuantity.round(0))
}
